import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Collapse,
  Badge,
  Button,
  InputAdornment,
  Menu,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import WarningIcon from '@mui/icons-material/Warning';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';

import { formatText } from '../../common/TextFormatterProvider';
import { getStoredValueLabel } from '../../../utils/functionParameterUtils';
import { profileName, isActiveProfile, isDeletedProfile } from '../../../utils/profileDisplay';

// One active profile row: the name/status block plus the three-dot menu button.
// Shared by the search-results list and the grouped list — `fileCount` is null in
// search results, which show the name without a file count.
function ProfileRow({ profile, selected, fileCount, secondary, statusIcon, indent, onSelect, onMenuOpen }) {
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(profile)}
      sx={{ pl: indent, pr: 0.5 }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <ArticleIcon />
      </ListItemIcon>
      <ListItemText
        sx={{ minWidth: 0 }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {profileName(profile)}
            </Typography>
            {fileCount !== null && (
              <Typography variant="caption" color="text.secondary">
                ({fileCount})
              </Typography>
            )}
          </Box>
        }
        secondary={secondary}
        secondaryTypographyProps={{ noWrap: true }}
      />
      {statusIcon}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onMenuOpen(e, profile);
        }}
        sx={{ opacity: 0.7 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </ListItemButton>
  );
}

// The group of profiles with no value for the grouped category. Not a word a
// person could store as a value (see reservedNameError), so it never collides.
const MISSING_GROUP = '\u0000missing';

function DataProfilesSidebar({
  dataProfiles,
  selectedProfile,
  selectedView,
  onProfileSelect,
  onEditProfile,
  onCopyProfile,
  onDeleteProfile,

  searchQuery,
  onSearchChange,
  groupBy,
  onGroupByChange,
  profileStats,
  attributeCategories,
  onBack
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [deletedExpanded, setDeletedExpanded] = useState(false);
  const [menuState, setMenuState] = useState(null); // { anchorEl, profile }

  const closeMenu = () => setMenuState(null);
  const openMenu = (e, profile) => setMenuState({ anchorEl: e.currentTarget, profile });

  // Active/deleted split — grouping and the main list are over active profiles only.
  const activeProfiles = useMemo(() => dataProfiles.filter(isActiveProfile), [dataProfiles]);
  const deletedProfiles = useMemo(() => dataProfiles.filter(isDeletedProfile), [dataProfiles]);

  // Group profiles by selected metadata category. Groups are keyed by the
  // value's label in a prototype-less object, so a stored value spelled like
  // an object member ("constructor") is a group and never a function; a
  // profile with no value for the category goes under its own internal key,
  // shown as "Unassigned", so a literal value "Unassigned" cannot merge with it.
  const groupedProfiles = useMemo(() => {
    if (!groupBy) {
      return { ungrouped: activeProfiles };
    }

    const grouped = Object.create(null);
    activeProfiles.forEach(profile => {
      const hasGroupValue = Object.prototype.hasOwnProperty.call(profile.default_tags || {}, groupBy);
      const groupValue = hasGroupValue ? getStoredValueLabel(profile.default_tags[groupBy]) : MISSING_GROUP;
      if (!grouped[groupValue]) {
        grouped[groupValue] = [];
      }
      grouped[groupValue].push(profile);
    });

    return grouped;
  }, [activeProfiles, groupBy]);

  // Shared search predicate: display name, each default's stored spelling, and
  // — for a Yes/No default — the word the sidebar shows for it, matched from
  // its first letter ("y", "ye", "yes" find a Yes; "n", "no" find a No).
  const matchesQuery = (profile, query) => {
    if (profileName(profile).toLowerCase().includes(query)) return true;
    return Object.values(profile.default_tags || {}).some((value) => {
      if (typeof value === 'boolean' && (value ? 'yes' : 'no').startsWith(query)) return true;
      return String(value).toLowerCase().includes(query);
    });
  };

  // Filter active profiles based on search
  const filteredProfiles = useMemo(() => {
    if (!searchQuery) return groupedProfiles;

    const query = searchQuery.toLowerCase();
    const filtered = Object.create(null);

    Object.entries(groupedProfiles).forEach(([group, profiles]) => {
      const matchingProfiles = profiles.filter(profile => matchesQuery(profile, query));

      if (matchingProfiles.length > 0) {
        filtered[group] = matchingProfiles;
      }
    });

    return filtered;
  }, [groupedProfiles, searchQuery]);

  // Deleted profiles matching the search (flat list; rendered below active matches).
  const filteredDeletedProfiles = useMemo(() => {
    if (!searchQuery) return [];
    const query = searchQuery.toLowerCase();
    return deletedProfiles.filter(profile => matchesQuery(profile, query));
  }, [deletedProfiles, searchQuery]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const getProfileStatusIcon = (profileId) => {
    const stats = profileStats[profileId];
    if (!stats) return null;

    if (stats.needsAttention > 0) {
      return (
        <Badge badgeContent={stats.needsAttention} color="warning">
          <WarningIcon color="warning" fontSize="small" />
        </Badge>
      );
    }
    return null;
  };

  const getProfileStatusText = (profileId) => {
    const stats = profileStats[profileId];
    if (!stats || stats.totalFiles === 0) return 'No files';
    
    if (stats.needsAttention > 0) {
      return `${stats.needsAttention} need attention`;
    } else if (stats.processing > 0) {
      return `${stats.processing} processing`;
    } else {
      return 'All processed';
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.03)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">Back</Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search profiles..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        {/* Group By */}
        <FormControl fullWidth size="small">
          <InputLabel>Group by</InputLabel>
          <Select
            value={groupBy}
            onChange={(e) => onGroupByChange(e.target.value)}
            label="Group by"
          >
            <MenuItem value="">None</MenuItem>
            {attributeCategories.map(category => (
              <MenuItem key={category} value={category}>
                {formatText(category)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Profiles List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List>
          {/* Home Item */}
          <ListItemButton
            selected={selectedView === 'home'}
            onClick={() => onProfileSelect(null)}
            sx={{ 
              mb: 1,
              borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
            }}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Files Home"
              primaryTypographyProps={{ fontWeight: 'medium' }}
            />
          </ListItemButton>

          {searchQuery ? (
            // Search Results View
            <>
              <ListItem>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Search Results: "{searchQuery}"
                </Typography>
              </ListItem>
              
              {Object.entries(filteredProfiles).map(([group, profiles]) => (
                <React.Fragment key={group}>
                  {groupBy && (
                    <ListItem>
                      <Typography variant="caption" sx={{ px: 2 }}>
                        {formatText(group)}
                      </Typography>
                    </ListItem>
                  )}
                  {profiles.map(profile => (
                    <ProfileRow
                      key={profile.id}
                      profile={profile}
                      selected={selectedProfile?.id === profile.id}
                      fileCount={null}
                      secondary={getProfileStatusText(profile.id)}
                      statusIcon={getProfileStatusIcon(profile.id)}
                      indent={groupBy ? 4 : 2}
                      onSelect={onProfileSelect}
                      onMenuOpen={openMenu}
                    />
                  ))}
                </React.Fragment>
              ))}

              {/* Deleted matches — below the active matches, with deleted styling */}
              {filteredDeletedProfiles.length > 0 && (
                <>
                  <ListItem>
                    <Typography variant="caption" sx={{ px: 2 }}>
                      Deleted
                    </Typography>
                  </ListItem>
                  {filteredDeletedProfiles.map(profile => (
                    <ListItemButton
                      key={profile.id}
                      selected={selectedProfile?.id === profile.id}
                      onClick={() => onProfileSelect(profile)}
                      sx={{ pl: 2, opacity: 0.6 }}
                    >
                      <ListItemIcon>
                        <ArticleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={profileName(profile)}
                        secondary={getProfileStatusText(profile.id)}
                      />
                      {getProfileStatusIcon(profile.id)}
                    </ListItemButton>
                  ))}
                </>
              )}
            </>
          ) : (
            // Normal View
            <>
              <ListItem>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
                  Data Profiles ({activeProfiles.length})
                </Typography>
              </ListItem>

              {Object.entries(filteredProfiles).map(([group, profiles]) => {
                const isExpanded = !groupBy
                  || (Object.prototype.hasOwnProperty.call(expandedGroups, group) && expandedGroups[group]);

                return (
                  <React.Fragment key={group}>
                    {groupBy && (
                      <ListItemButton onClick={() => toggleGroup(group)}>
                        <ListItemIcon>
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </ListItemIcon>
                        <ListItemIcon>
                          <FolderIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={group === MISSING_GROUP ? 'Unassigned' : formatText(group)}
                          secondary={`${profiles.length} profiles`}
                        />
                      </ListItemButton>
                    )}

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {profiles.map(profile => {
                          const stats = profileStats[profile.id];
                          const totalFiles = stats?.totalFiles || 0;

                          return (
                            <ProfileRow
                              key={profile.id}
                              profile={profile}
                              selected={selectedProfile?.id === profile.id}
                              fileCount={totalFiles}
                              secondary={getProfileStatusText(profile.id)}
                              statusIcon={getProfileStatusIcon(profile.id)}
                              indent={groupBy ? 4 : 2}
                              onSelect={onProfileSelect}
                              onMenuOpen={openMenu}
                            />
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}

              {/* Deleted section — flat, collapsible, styled */}
              {deletedProfiles.length > 0 && (
                <>
                  <ListItemButton onClick={() => setDeletedExpanded(prev => !prev)}>
                    <ListItemIcon>
                      {deletedExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </ListItemIcon>
                    <ListItemText primary={`Deleted (${deletedProfiles.length})`} />
                  </ListItemButton>
                  <Collapse in={deletedExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {deletedProfiles.map(profile => (
                        <ListItemButton
                          key={profile.id}
                          selected={selectedProfile?.id === profile.id}
                          onClick={() => onProfileSelect(profile)}
                          sx={{ pl: 2, opacity: 0.6 }}
                        >
                          <ListItemIcon>
                            <ArticleIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={profileName(profile)}
                            secondary={getProfileStatusText(profile.id)}
                          />
                          {getProfileStatusIcon(profile.id)}
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}
            </>
          )}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.03)' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            // Trigger create profile dialog
            const event = new CustomEvent('create-profile');
            document.dispatchEvent(event);
          }}
        >
          Create Profile
        </Button>
      </Box>

      {/* Row actions — one menu for every active profile row. */}
      <Menu
        anchorEl={menuState?.anchorEl}
        open={!!menuState}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            onEditProfile(menuState.profile);
            closeMenu();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            onCopyProfile(menuState.profile);
            closeMenu();
          }}
        >
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          Copy
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            onDeleteProfile(menuState.profile);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default DataProfilesSidebar;
